import { Button, InputAdornment, OutlinedInput, Tooltip, Typography } from "@mui/material";
import React from "react";
import { ModalContext } from "../../../../Components/Modals";
import { GridRowsProp, GridColDef, GridRowParams, GridActionsCellItem } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import { setupService } from "../../../../Core/Api/services";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { useNavigate } from "react-router";
import { routePaths } from "../../../../Router/router";
import TableCellChip from "../../../../Components/Table/TableCellChip/table-cell-chip";
import { DbMetadataViewInfos } from "../../../../Core/Api/Types/db-metadata-view-infos";
import { DbMetadataViewGroupRow } from "../../../../Core/Api/Types/db-metadata-view-group-row";
import { DbMetadataViewGroup } from "../../../../Core/Api/Types/db-metadata-view-group";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { GetEnumValueLabelMap, UserHasAcl } from "../../../../Utils/helpers";
import { useTranslation } from "react-i18next";
import { DeleteOutlineOutlined } from "@mui/icons-material";
import TableUuids from "../../../../Components/Table/table-uuids";
import { ObjectLayer } from "../../../../Core/Api/Types/object-layer";
import { ObjectType } from "../../../../Core/Api/Types/object-type";
import { MetadataViewType } from "../../../../Core/Api/Types/metadata-view-type";

function AdminSettingsMetadataViewsManagement() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(true);
  const [rows, setRows] = React.useState<DbMetadataViewInfos[]>([]);
  const [textFilter, setTextFilter] = React.useState<string>("");
  const [trigger, setTrigger] = React.useState<boolean>(false);
  const enqueueCustomSnackbar = useCustomSnackbar();
  const navigate = useNavigate();

  // Table rows
  const apiGrid = useGridApiRef();

  // Modal
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  React.useEffect(() => {
    setupService.GetAllMetadataViewsInfo().then((res) => {
      setRows(res.data || []);
      setIsLoading(false);
    });
  }, [trigger]);

  // #region Grid definition
  let _rows: GridRowsProp = [];
  type Row = (typeof _rows)[number];

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("common:name"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      renderCell: (data: Row) => (
        <Typography title={data.value} variant="body1">
          {data.value || "-"}
        </Typography>
      ),
    },
    {
      field: "objectLayer",
      headerName: t("field:label.objectLayer"),
      flex: CellSize.small,
      minWidth: CellSize.small,
      type: "singleSelect",
      valueOptions: GetEnumValueLabelMap(ObjectLayer),
      renderCell: (data: Row) => <TableCellChip data={{ value: [data.value ?? "-"] }} />,
    },
    {
      field: "objectTypes",
      headerName: t("common:objectTypesUpperCase"),
      flex: CellSize.large,
      minWidth: CellSize.large,
      type: "singleSelect",
      valueOptions: GetEnumValueLabelMap(ObjectType),
      renderCell: (data: Row) => <TableCellChip data={data} />,
    },
    {
      field: "type",
      headerName: t("common:type"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      type: "singleSelect",
      valueOptions: GetEnumValueLabelMap(MetadataViewType),
      renderCell: (data: Row) => <TableCellChip data={{ value: [data.value ?? "-"] }} />,
    },
    {
      field: "actions",
      headerName: "",
      type: "actions",
      flex: CellSize.actionsDefault,
      minWidth: CellSize.actionsDefault,
      align: "right",
      getActions: (params: GridRowParams) => [
        <GridActionsCellItem
          icon={
            <Tooltip title={t("common:copy")}>
              <FileCopyIcon sx={{ width: 20, height: 20, color: "text.primary" }} />
            </Tooltip>
          }
          label={t("common:copy")}
          title={t("common:copy")}
          disabled={!UserHasAcl(AclTypes.aclSuperUser)}
          onClick={() => {
            navigate(routePaths.createMetadataViews.path, { state: { ...params.row, id: -500 } });
          }}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title={t("common:editUpperCase")}>
              <EditIcon sx={{ width: 20, height: 20, color: "primary.text" }} />
            </Tooltip>
          }
          label={t("common:modify")}
          title={t("common:modify")}
          disabled={!UserHasAcl(AclTypes.aclSuperUser)}
          onClick={() => {
            navigate(routePaths.editMetadataViews.path, { state: params.row });
          }}
        />,
        <GridActionsCellItem
          icon={
            <Tooltip title={t("common:delete")}>
              <DeleteOutlineOutlined sx={{ width: 20, height: 20, color: "error.main" }} />
            </Tooltip>
          }
          label={t("common:delete")}
          title={t("common:delete")}
          disabled={!UserHasAcl(AclTypes.aclSuperUser)}
          onClick={() => {
            if (!openModal) return;

            openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
              displayComment: true,
              commentLabel: t("modal:comment.confirmDeleteMetadataView"),
              title: t("modal:title.deleteMetadataView"),
              onConfirm: () => {
                setupService.DeleteMetadataView(params?.row?.id).then(() => {
                  enqueueCustomSnackbar(t("alert:success.metadataViewDeleted"), "success");
                  setTrigger((prevState) => !prevState);
                });
              },
            });
          }}
        />,
      ],
    },
  ];
  // #endregion

  const additionalActions = (
    <Button
      variant="contained"
      color="primary"
      startIcon={<AddIcon />}
      onClick={() => {
        navigate(routePaths.createMetadataViews.path, {
          state: new DbMetadataViewInfos({
            groups: [
              new DbMetadataViewGroup({
                icon: "",
                name: { ["en-UK"]: "" },
                id: -500,
                rows: [
                  new DbMetadataViewGroupRow({
                    columns: [],
                  }),
                ],
              }),
            ],
            filters: [],
            id: -500,
            name: "",
          }),
        });
      }}
    >
      {t("button:newMetadataView")}
    </Button>
  );

  return (
    <DataTable
      loading={isLoading}
      tableKey={TableUuids.SETTINGS_METADATA_VIEWS}
      columns={columns}
      rows={rows}
      rowHeight={64}
      columnHeaderHeight={40}
      pagination={true}
      additionalActions={additionalActions}
      apiRef={apiGrid}
      getRowHeight={() => "auto"}
      onRowClick={(params) => {
        navigate(routePaths.editMetadataViews.path, { state: params.row });
      }}
    />
  );
}

export default AdminSettingsMetadataViewsManagement;
